import { useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DOC_TYPES, slug } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const VerifyIcon = ({ s }) =>
  s === "Verified" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : s === "Rejected" ? <XCircle className="h-4 w-4 text-red-500" /> : <Clock className="h-4 w-4 text-amber-500" />;

export const DocumentList = ({ documents }) => (
  <ul className="space-y-2" data-testid="document-list">
    {documents.map((d) => (
      <li key={d.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`document-item-${d.id}`}>
        <span className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 text-slate-500 shrink-0" />
          <span className="font-medium text-slate-800">{d.document_type}</span>
          <span className="text-slate-500 truncate">{d.original_filename}</span>
        </span>
        <span className="flex items-center gap-1 text-xs text-slate-600 shrink-0" data-testid={`document-status-${d.id}`}>
          <VerifyIcon s={d.verification_status} /> {d.verification_status}
        </span>
      </li>
    ))}
  </ul>
);

export const FileUploader = ({ applicationId, onUploaded }) => {
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("document_type", docType);
      fd.append("file", file);
      try {
        const { data } = await api.post(`/applications/${applicationId}/documents`, fd);
        toast.success(`${docType} uploaded securely`);
        onUploaded?.(data);
      } catch (e) {
        toast.error(formatApiError(e, "Upload failed"));
      }
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="file-uploader">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-700">Document type</span>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="w-56 bg-white" data-testid="document-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOC_TYPES.map((t) => (
              <SelectItem key={t} value={t} data-testid={`document-type-option-${slug(t)}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        role="button"
        tabIndex={0}
        data-testid="file-dropzone"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-[#FF6B00] bg-orange-50" : "border-slate-300 bg-slate-50 hover:border-[#FF6B00]"}`}
      >
        {busy ? <Loader2 className="mx-auto h-8 w-8 text-[#FF6B00] animate-spin" /> : <UploadCloud className="mx-auto h-8 w-8 text-[#FF6B00]" />}
        <p className="mt-3 text-sm font-medium text-slate-800">Drag & drop your {docType} here, or click to browse</p>
        <p className="mt-1 text-xs text-slate-500">PDF, JPG or PNG · up to 10MB · 256-bit encrypted storage</p>
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden" data-testid="file-input" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      </div>
    </div>
  );
};
